// src/components/ui/index.js
// Small presentational building blocks shared by the dashboard pages.
// Styles live in src/styles/components.css; colours come from the active theme.
import React from 'react';
import { Skeleton } from 'antd';

export const Page = ({ children, className = '' }) => (
  <div className={`page ${className}`.trim()}>{children}</div>
);

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="page-header">
    <div className="page-header__text">
      <h1 className="page-header__title">{title}</h1>
      {subtitle && <p className="page-header__subtitle">{subtitle}</p>}
    </div>
    {actions && <div className="page-header__actions">{actions}</div>}
  </div>
);

export const SectionHead = ({ title, subtitle, actions }) => (
  <div className="section-head">
    <div>
      <h2 className="section-title">{title}</h2>
      {subtitle && <p className="section-subtitle">{subtitle}</p>}
    </div>
    {actions && <div className="page-header__actions">{actions}</div>}
  </div>
);

// Two-line card title: main label plus an optional muted sub-line
export const CardTitle = ({ title, subtitle }) => (
  <span className="card-title">
    <span className="card-title__main">{title}</span>
    {subtitle && <span className="card-title__sub">{subtitle}</span>}
  </span>
);

export const StatGrid = ({ columns = 4, children }) => (
  <div className="stat-grid" data-cols={columns}>
    {children}
  </div>
);

// tone: 'primary' | 'positive' | 'warning' | 'danger' | 'neutral'
// meter: optional 0..1 share shown as a thin bar
export const StatCard = ({
  label,
  value,
  prefix,
  unit,
  icon,
  tone = 'primary',
  meter,
  caption,
  loading = false,
  compact = false,
}) => (
  <div className="stat-card" data-tone={tone}>
    <div className="stat-card__top">
      <span className="stat-card__label">{label}</span>
      {icon && <span className="stat-card__icon">{icon}</span>}
    </div>
    {loading ? (
      <Skeleton.Button active size="small" style={{ width: 90, height: 28 }} />
    ) : (
      <div className={`stat-card__value${compact ? ' stat-card__value--sm' : ''}`}>
        {prefix && <span className="stat-card__prefix">{prefix}</span>}
        {value}
        {unit && <span className="stat-card__unit">{unit}</span>}
      </div>
    )}
    {typeof meter === 'number' && (
      <div className="stat-card__meter" role="presentation">
        <span style={{ width: `${Math.max(0, Math.min(1, meter || 0)) * 100}%` }} />
      </div>
    )}
    {caption && <div className="stat-card__caption">{caption}</div>}
  </div>
);

export const EmptyState = ({ icon, title, description, action, plain = false }) => (
  <div className={`empty-state${plain ? ' empty-state--plain' : ''}`}>
    {icon && <span className="empty-state__icon">{icon}</span>}
    {title && <h3 className="empty-state__title">{title}</h3>}
    {description && <p className="empty-state__text">{description}</p>}
    {action}
  </div>
);

// solid: filled with the tone colour, for final states such as "Paid"
// plain: no status dot, for counts
export const StatusPill = ({ tone = 'neutral', solid = false, plain = false, children }) => (
  <span
    className={`status-pill${solid ? ' status-pill--solid' : ''}${plain ? ' status-pill--plain' : ''}`}
    data-tone={tone}
  >
    {children}
  </span>
);

export const initialsOf = (name = '') =>
  name
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

export const PersonCell = ({ name, sub }) => (
  <div className="person-cell">
    <span className="person-cell__avatar" aria-hidden="true">
      {initialsOf(name)}
    </span>
    <span className="person-cell__text">
      <span className="person-cell__name">{name}</span>
      {sub && <span className="person-cell__sub">{sub}</span>}
    </span>
  </div>
);

// Rupee amount formatted with thousands separators, e.g. "Rs 12,450"
export const formatRs = (amount, decimals = 0) => {
  const n = Number(amount) || 0;
  return `Rs ${n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};
